import React, { useEffect } from "react";
import { StyleSheet, ScrollView, View } from "react-native";
import { router, usePathname } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { RadioButtonGroup } from "@/components/ui/RadioButtonGroup";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { useApply } from "@/contexts/ApplyContext";

const STEP_INDEX = 0;

export default function EligibilityScreen() {
  const { colors } = useTheme();
  const { formData, updateFormData, updateStep, hasSavedData, isLoading } = useApply();
  const pathname = usePathname();

  // Only redirect if we're actually on this screen (tabs keep screens mounted)
  const isOnThisScreen = pathname === "/apply" || pathname.endsWith("/apply/");

  // Redirect to welcome-back if there's saved data
  useEffect(() => {
    if (!isLoading && hasSavedData && isOnThisScreen) {
      router.replace("/apply/welcome-back");
    }
  }, [isLoading, hasSavedData, isOnThisScreen]);

  // Track current step
  useEffect(() => {
    if (!isLoading && !hasSavedData && isOnThisScreen) {
      updateStep(STEP_INDEX);
    }
  }, [isLoading, hasSavedData, isOnThisScreen, updateStep]);

  const canContinue = formData.authorizedToWork !== null && formData.over18 !== null;
  const isEligible = formData.authorizedToWork === true && formData.over18 === true;

  const handleContinue = () => {
    if (isEligible) {
      router.push("/apply/info");
    } else {
      router.push("/apply/ineligible");
    }
  };

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <View style={styles.header}>
          <MaterialIcons name="verified-user" size={48} color={colors.primary} />
          <ThemedText style={[styles.title, { color: colors.text }]}>Eligibility Check</ThemedText>
          <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
            Before we begin, please confirm the following requirements
          </ThemedText>
        </View>

        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.question}>
            <ThemedText style={[styles.questionLabel, { color: colors.text }]}>
              Are you authorized to work in the United States?
            </ThemedText>
            <ThemedText style={[styles.requiredText, { color: colors.ruby?.text || colors.textSecondary }]}>
              Required *
            </ThemedText>
            <RadioButtonGroup
              options={["Yes", "No"]}
              value={formData.authorizedToWork === null ? undefined : formData.authorizedToWork ? "Yes" : "No"}
              onChange={(item) => {
                updateFormData({ authorizedToWork: item === "Yes" });
              }}
              orientation="horizontal"
              style={styles.toggle}
            />
          </View>

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <View style={styles.question}>
            <ThemedText style={[styles.questionLabel, { color: colors.text }]}>Are you over the age of 18?</ThemedText>
            <ThemedText style={[styles.requiredText, { color: colors.ruby?.text || colors.textSecondary }]}>
              Required *
            </ThemedText>
            <RadioButtonGroup
              options={["Yes", "No"]}
              value={formData.over18 === null ? undefined : formData.over18 ? "Yes" : "No"}
              onChange={(item) => {
                updateFormData({ over18: item === "Yes" });
              }}
              orientation="horizontal"
              style={styles.toggle}
            />
          </View>
        </View>

        {canContinue && !isEligible && (
          <View style={[styles.warningCard, { backgroundColor: colors.ruby?.background || "#FEE2E2" }]}>
            <MaterialIcons name="warning" size={24} color={colors.ruby?.text || "#DC2626"} />
            <ThemedText style={[styles.warningText, { color: colors.ruby?.text || "#DC2626" }]}>
              To apply for a position with RockForce, you must be authorized to work in the United States and be at
              least 18 years of age.
            </ThemedText>
          </View>
        )}

        <Button
          text="Continue"
          icon="arrow-right"
          iconPosition="right"
          iconSize={18}
          iconColor="white"
          onPress={handleContinue}
          enabled={canContinue}
          style={[
            styles.continueButton,
            {
              backgroundColor: canContinue ? colors.primary : colors.border,
              opacity: canContinue ? 1 : 0.5,
            },
          ]}
          textStyle={styles.continueButtonText}
        />

        <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <MaterialIcons name="info-outline" size={20} color={colors.textSecondary} />
          <ThemedText style={[styles.infoText, { color: colors.textSecondary }]}>
            Your information is kept confidential and is only used for employment eligibility verification purposes.
          </ThemedText>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
    gap: 24,
  },
  header: {
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
  },
  question: {
    gap: 8,
  },
  questionLabel: {
    fontSize: 16,
    fontWeight: "600",
    lineHeight: 24,
  },
  requiredText: {
    fontSize: 12,
  },
  toggle: {
    marginTop: 8,
  },
  divider: {
    height: 1,
    marginVertical: 20,
  },
  warningCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    padding: 16,
    borderRadius: 12,
  },
  warningText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  continueButton: {
    borderRadius: 8,
    minHeight: 52,
  },
  continueButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  infoCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
});
